import React, { useState } from "react";
import { Activity, Bot, ChevronDown, ChevronUp, Wifi, WifiOff } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  humanize,
  resolveEntryState,
  resolveProtectionState,
  toneClasses,
} from "./TradingTruthBar";

/**
 * Dense one-line replacement for the five-card trading status grid.
 *
 * The grid spent roughly 90px of vertical space to say "nothing is connected",
 * squeezing the chart into under half the viewport. This says the same thing
 * in one row, carries severity in colour, and expands on demand for the full
 * detail. It deliberately reuses TradingTruthBar's state resolvers so the two
 * presentations can never disagree about whether entries are allowed or a
 * position is protected.
 */

const dotTone = {
  good: "bg-emerald-400",
  warn: "bg-amber-400",
  bad: "bg-red-400",
  neutral: "bg-white/40",
};

const Chip = ({ tone = "neutral", icon: Icon, label, value, title }) => (
  <span
    title={title || `${label}: ${value}`}
    className={cn(
      "inline-flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]",
      toneClasses[tone] || toneClasses.neutral,
    )}
  >
    {Icon ? <Icon className="h-3 w-3 shrink-0 opacity-70" /> : (
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotTone[tone] || dotTone.neutral)} />
    )}
    <span className="hidden shrink-0 opacity-60 sm:inline">{label}</span>
    <span className="truncate font-medium">{value}</span>
  </span>
);

const ChartStatusStrip = ({
  accountName,
  accountActive = true,
  accountCanTrade = true,
  brokerConnected,
  brokerStateKnown,
  symbol,
  contractLabel,
  strategyName,
  strategyTimeframe,
  chartTimeframe,
  marketLive,
  marketQuality,
  autoTradeEnabled,
  inAutomationScope,
  hasActiveLink,
  hasOpenPosition,
  unsafePosition,
  protectionConfirmed,
  protectionPending,
  latestDecision,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);

  const entry = resolveEntryState({
    hasAccount: Boolean(accountName),
    accountActive,
    accountCanTrade,
    brokerConnected,
    brokerStateKnown,
    autoTradeEnabled,
    inAutomationScope,
    hasActiveLink,
  });
  const protection = resolveProtectionState({
    hasOpenPosition,
    unsafePosition,
    protectionConfirmed,
    protectionPending,
  });

  const brokerTone = brokerConnected && brokerStateKnown ? "good" : accountName ? "bad" : "warn";
  const strategyTime = Number(strategyTimeframe) > 0 ? `${strategyTimeframe}m` : "not linked";
  const chartTime = chartTimeframe || "—";
  const timeframesMatch =
    Number(strategyTimeframe) > 0 && String(chartTime).toLowerCase() === `${strategyTimeframe}m`;

  // Anything not "good" is worth surfacing without expanding.
  const worstTone = [brokerTone, entry.tone, protection.tone].includes("bad")
    ? "bad"
    : [brokerTone, entry.tone, protection.tone].includes("warn")
      ? "warn"
      : "good";

  return (
    <section
      aria-label="Trading status"
      className={cn(
        "rounded-md border bg-black/20",
        worstTone === "bad"
          ? "border-red-500/25"
          : worstTone === "warn"
            ? "border-amber-500/25"
            : "border-emerald-500/20",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5">
        <Chip
          tone={brokerTone}
          icon={brokerConnected && brokerStateKnown ? Wifi : WifiOff}
          label="Account"
          value={accountName || "None selected"}
          title={
            brokerConnected
              ? brokerStateKnown
                ? "Connected — broker state confirmed"
                : "Connected — broker state unknown"
              : "Broker unavailable"
          }
        />
        <Chip
          tone={hasActiveLink ? "neutral" : "warn"}
          icon={Activity}
          label="Scope"
          value={`${contractLabel || symbol || "—"} · ${strategyName || "no strategy"} · ${chartTime}`}
          title={`Signal ${strategyTime} · chart ${chartTime}${timeframesMatch ? "" : " (different views)"}`}
        />
        <Chip tone={entry.tone} label="Entries" value={entry.value} title={entry.detail} />
        <Chip
          tone={protection.tone}
          label="Protection"
          value={protection.value}
          title={protection.detail}
        />
        <Chip
          tone="neutral"
          icon={Bot}
          label="Last decision"
          value={humanize(latestDecision?.decision || latestDecision?.status)}
          title={humanize(
            latestDecision?.reason_code,
            marketLive ? marketQuality : `Market data: ${marketQuality || "delayed"}`,
          )}
        />

        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide status detail" : "Show status detail"}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-white/10 px-1.5 py-1 text-[10px] text-white/50 transition hover:bg-white/5 hover:text-white/80"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Less" : "Detail"}
        </button>
      </div>

      {expanded ? (
        <div className="grid gap-2 border-t border-white/10 px-3 py-2 text-[11px] text-white/60 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Account and broker</div>
            <div className="text-white/80">{accountName || "No account selected"}</div>
            <div>
              {brokerConnected
                ? brokerStateKnown
                  ? "Connected — broker state confirmed"
                  : "Connected — broker state unknown"
                : "Broker unavailable"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Instrument and strategy</div>
            <div className="text-white/80">
              {contractLabel || symbol || "No instrument"} — {strategyName || "No active strategy"}
            </div>
            <div>
              Signal {strategyTime} · chart {chartTime}
              {timeframesMatch ? "" : " (different views)"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">New entries</div>
            <div className="text-white/80">{entry.value}</div>
            <div>{entry.detail}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Position protection</div>
            <div className="text-white/80">{protection.value}</div>
            <div>{protection.detail}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Last automation decision</div>
            <div className="text-white/80">
              {humanize(latestDecision?.decision || latestDecision?.status)}
            </div>
            <div>
              {humanize(
                latestDecision?.reason_code,
                marketLive ? marketQuality : `Market data: ${marketQuality || "delayed"}`,
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default React.memo(ChartStatusStrip);
